import { defineComponent as _defineComponent } from 'vue'
import { ref, computed, inject } from "vue";
import dayjs from "dayjs";
import {
  PROVIDE_KEY_SHOW_ERROR_MODAL,
  ShowErrorModal,
  PROVIDE_KEY_SHOW_TOAST_MESSAGE,
  ShowToastMessage,
  BookStatusName,
  CalendarDisplayMode,
  BookIntervalCode,
  CalendarEventInfo,
  BookStatus,
  FILTER_KEY_PERIOD,
  FILTER_KEY_TYPE,
  FILTER_KEY_SORT,
  RouterName,
} from "@/types";
import { IBookInfo } from "@/domain/entities/book";
import Page from "@/components/shared/Page.vue";
import NavigationBar from "@/components/shared/NavigationBar.vue";
import SegmentedControl from "@/components/shared/SegmentedControl.vue";
import BookHistoryDetail from "@/views/book/BookHistoryDetail.vue";
import FilterModal from "@/components/shared/modal/FilterModal.vue";
import { IBoardInfo } from "@/domain/entities/board";
import BookHistoryTable from "@/components/book/BookHistoryTable.vue";
import BoardHistoryTable from "@/components/board/BoardHistoryTable.vue";
import { useBoardStore } from "@/stores/boardStore";
import Calendar from "@/components/shared/Calendar.vue";
import BoardHistoryDetail from "../board/BoardHistoryDetail.vue";
import { useConvertBookInfoToEventList } from "@/composables/book/useConvertBookInfoToEventList";
import EventCalendar from "@/components/shared/EventCalendar.vue";
import BookHistoryRow from "@/components/book/BookHistoryRow.vue";
import SelectableImageGroup from "@/components/shared/SelectableImageGroup.vue";
import { useGetFilteredBookInfos } from "@/composables/book/useGetFilteredBookInfos";
import { useGetBookInfoRequest } from "@/composables/book/useGetBookInfoRequest";
import BookHistoryListModal from "@/components/book/BookHistoryListModal.vue";
import router from "@/router";
import PageModal from "@/components/shared/modal/base/PageModal.vue";


const __default__ = {
  name: "BookAndBoardHistory",
};

export default /*#__PURE__*/_defineComponent({
  ...__default__,
  setup(__props, { expose: __expose }) {
  __expose();

const boardStore = useBoardStore();

const showErrorModal = inject(PROVIDE_KEY_SHOW_ERROR_MODAL) as ShowErrorModal;
const showToastMessage = inject(
  PROVIDE_KEY_SHOW_TOAST_MESSAGE
) as ShowToastMessage;

const segmentTitles = ["예약내역", "탑승내역"];
const currentSegmentIndex = ref(0);

/** Swipe 처리 */
const onChangedSegmentIndex = (index: number) => {
  currentSegmentIndex.value = index;
  if (index === 0) {
    getBookInfos();
  } else {
    getBoardInfos();
  }
};

let touchStartX = 0;
let touchEndX = 0;
const segmentedControl = ref<InstanceType<typeof SegmentedControl>>();
const onTouchStart = (event: TouchEvent) => {
  touchStartX = event.changedTouches[0].screenX;
};
const onTouchEnd = (event: TouchEvent) => {
  touchEndX = event.changedTouches[0].screenX;
  if (Math.abs(touchEndX - touchStartX) < 100) {
    return;
  }
  currentSegmentIndex.value = touchEndX < touchStartX ? 1 : 0;
  segmentedControl.value?.changeSegmentIndex(currentSegmentIndex.value);
};

/** 화면 유형(달력, 목록)을 선택하는 로직 */
const DISPLAY_TYPE_CALENDAR = "달력";
const DISPLAY_TYPE_LIST = "목록";
const displayTypeMap = new Map([
  [
    DISPLAY_TYPE_CALENDAR,
    {
      image: require("@/assets/icons/calendar-unselected.svg"),
      selectedImage: require("@/assets/icons/calendar-selected.svg"),
      altText: "달력으로 보기",
    },
  ],
  [
    DISPLAY_TYPE_LIST,
    {
      image: require("@/assets/icons/list-unselected.svg"),
      selectedImage: require("@/assets/icons/list-selected.svg"),
      altText: "목록으로 보기",
    },
  ],
]);

const displayTypeImageList = [...displayTypeMap.values()].map(
  (displayType) => displayType.image
);

const displayTypeSelectedImageList = [...displayTypeMap.values()].map(
  (displayType) => displayType.selectedImage
);

const imageAltTextList = [...displayTypeMap.values()].map(
  (displayType) => displayType.altText
);

const selectedDisplayTypeIndexes = ref<number[]>([0]);
const currentDisplayType = ref(DISPLAY_TYPE_CALENDAR);

const selectDisplayType = (index: number) => {
  selectedDisplayTypeIndexes.value = [index];
  currentDisplayType.value = [...displayTypeMap.keys()][index];
  if (currentDisplayType.value !== DISPLAY_TYPE_CALENDAR) {
    resetCurrentMonth();
  }
  getBookInfos();
};

/** 필터 로직 */
const shouldShowFilters = ref(false);

const showFilterModal = () => {
  shouldShowFilters.value = true;
};

const currentFilters = ref<Map<string, string>>(new Map());
currentFilters.value.set(FILTER_KEY_PERIOD, "1개월");
currentFilters.value.set(FILTER_KEY_TYPE, "전체");
currentFilters.value.set(FILTER_KEY_SORT, "내림차순");

const filters = new Map();
filters.set(FILTER_KEY_PERIOD, ["1개월", "3개월", "6개월", "전체"]);
filters.set(FILTER_KEY_TYPE, ["전체", "예약", "탑승", "미탑승", "취소"]);
filters.set(FILTER_KEY_SORT, ["오름차순", "내림차순"]);

// 선택된 필터 문구
const currentFilterString = computed(() =>
  Array.from(currentFilters.value.values()).join(" · ")
);

// 예약내역 필터 처리
const onFilterChanged = async (filters: Map<string, string>) => {
  scrollToTop();
  shouldShowFilters.value = false;
  currentFilters.value = filters;
  getBookInfos();
};

// 스크롤 처리
const bookHistoryContentRef = ref<HTMLElement>();
const scrollToTop = () => {
  bookHistoryContentRef.value?.scrollTo({
    top: 0,
    behavior: "smooth",
  });
};

/** 예약내역 조회하기 */
const bookInfos = ref<IBookInfo[]>([]);

// 월예약 내역
const currentMonthlyBookInfos = computed((): IBookInfo[] =>
  bookInfos.value.filter(
    (bookInfo) => bookInfo.bookIntervalCode === BookIntervalCode.MONTH
  )
);

const { requestForCalendar, requestForPastBook } = useGetBookInfoRequest();
const request = computed(() => {
  if (currentDisplayType.value === DISPLAY_TYPE_CALENDAR) {
    return requestForCalendar(currentMonth.value, true);
  } else {
    return requestForPastBook(currentFilters.value);
  }
});

const { getFilteredBookInfos } = useGetFilteredBookInfos();
// 예약내역 가져오기 - 정렬(1개월, 전체, 오름차순)
const getBookInfos = async () => {
  const filterBookStatus = currentFilters.value.get(FILTER_KEY_TYPE);

  bookInfos.value = await getFilteredBookInfos(
    request.value,
    BookStatus.All,
    filterBookStatus
  );
};

/** 달력 화면 예약내역 */
// 달력 이벤트별 클래스 정의
const eventColorClassMap = new Map([
  [BookStatusName.BOOK, "book"],
  [BookStatusName.BOARD, "board"],
  [BookStatusName.CANCEL, "cancel"],
  [BookStatusName.NOSHOW, "noshow"],
]);

// 현재 예약내역에 맞는 달력 이벤트 목록
const currentCalendarEvents = computed(() =>
  useConvertBookInfoToEventList(bookInfos.value)
);

const calendarDisplayMode = computed(() => {
  const hasWeekend = currentCalendarEvents.value.some(
    (event) => dayjs(event.date).day() === 0 || dayjs(event.date).day() === 6
  );

  return hasWeekend ? CalendarDisplayMode.Bar : CalendarDisplayMode.Chip;
});

const currentMonth = ref(dayjs().format("YYYY-MM"));
const setCurrentMonth = (month: string) => {
  currentMonth.value = month;
};

const resetCurrentMonth = () => {
  currentMonth.value = dayjs().format("YYYY-MM");
};

// 달력에 월이 변경되었을 때 처리
const changedMonth = (month: string) => {
  setCurrentMonth(month);
  getBookInfos();
};

/** 탑승내역 조회하기 */
const boardInfos = ref<IBoardInfo[]>([]);
const isFetchedBoardHistory = ref(false);

const shouldShowEmptyContent = computed(() => {
  return !boardInfos.value.length && isFetchedBoardHistory.value;
});

// 달력 검색 범위 한계 설정 (오늘 일자로부터 1년 전까지만 선택 가능)
const limitFromBoardDate = ref(
  dayjs().subtract(1, "year").format("YYYY-MM-DD")
);
const limitToBoardDate = ref(dayjs().format("YYYY-MM-DD"));

/** 달력 */
const shouldShowFromDateCalendar = ref(false);
const shouldShowToDateCalendar = ref(false);

const showFromDateCalendar = () => {
  shouldShowFromDateCalendar.value = true;
};

const showToDateCalendar = () => {
  shouldShowToDateCalendar.value = true;
};

const onSelectFromDate = (dateStr: string) => {
  shouldShowFromDateCalendar.value = false;
  const fromDay = dayjs(dateStr);
  const endDay = dayjs(searchToDate.value);

  if (fromDay > endDay) {
    searchToDate.value = dateStr;
  }
  searchFromDate.value = dateStr;
  getBoardInfos();
};

const onSelectToDate = (dateStr: string) => {
  shouldShowToDateCalendar.value = false;
  const fromDay = dayjs(searchFromDate.value);
  const endDay = dayjs(dateStr);

  if (fromDay > endDay) {
    const getMonthAgo = dayjs(dateStr)
      .subtract(1, "month")
      .format("YYYY-MM-DD");
    searchFromDate.value = getMonthAgo;
  }
  searchToDate.value = dateStr;
  getBoardInfos();
};

// 검색 범위 설정 변수
const searchFromDate = ref(dayjs().subtract(1, "month").format("YYYY-MM-DD"));
const searchToDate = ref(dayjs().format("YYYY-MM-DD"));

// 탑승내역 가져오기 - 기간(한달전 ~ 오늘)
const getBoardInfos = async () => {
  const boardHistoryResult = await boardStore.getBoardingHistory(
    searchFromDate.value,
    searchToDate.value,
    "realOnDtm desc"
  );

  isFetchedBoardHistory.value = true;
  if (!boardHistoryResult.isSuccess) {
    showErrorModal(
      "탑승내역 오류",
      boardHistoryResult.errorMessage,
      boardHistoryResult.resultCode,
      boardHistoryResult.errorTime
    );
  }

  boardInfos.value = boardHistoryResult.data || [];
};

// 예약 상세 화면 보여주기
const shouldShowBookHistoryDetail = ref(false);
const toggleShowBookHistoryDetail = (show: boolean) => {
  shouldShowBookHistoryDetail.value = show;
};

const selectedBookInfo = ref<IBookInfo>();
const showBookHistoryDetail = (bookInfo: IBookInfo) => {
  selectedBookInfo.value = bookInfo;
  toggleShowBookHistoryDetail(true);
};

// 탑승내역 상세 화면 보여주기
const shouldShowBoardHistoryDetail = ref(false);
const toggleShowBoardHistoryDetail = (show: boolean) => {
  shouldShowBoardHistoryDetail.value = show;
};

const onCompleteSurvey = () => {
  toggleShowBoardHistoryDetail(false);
  getBoardInfos();
};

const selectedBoardInfo = ref<IBoardInfo>();
// const showBoardHistoryDetail = (boardInfo: IBoardInfo) => {
//   selectedBoardInfo.value = boardInfo;
//   shouldShowBoardHistoryDetail.value = true;
// };

const goToBoardEvaluation = (
  lineTurnUid: string,
  allocUid: string,
  lineTypeCode: string,
  lineName: string,
  campusName: string
) => {
  router.push({
    name: RouterName.BoardEvaluation,
    query: {
      lineTurnUid,
      allocUid,
      lineTypeCode,
      lineName,
      campusName,
    },
  });
};

const showBoardHistoryDetail = (boardInfo: IBoardInfo) => {
  // goToBoardEvaluation(
  //   boardInfo.lineTurnUid,
  //   boardInfo.allocUid,
  //   boardInfo.lineTypeCode,
  //   boardInfo.lineRouteName,
  //   boardInfo.campusName
  // );
};

// 선택된 날짜의 예약내역 목록을 보여준다.
const shouldShowBookHistoryListModal = ref(false);
const toggleShowBookHistoryListModal = (show: boolean) => {
  shouldShowBookHistoryListModal.value = show;
};

const selectedBookInfos = ref<IBookInfo[]>([]);
const showBookHistoryListModal = (event: CalendarEventInfo<IBookInfo>) => {
  selectedBookInfos.value = event.detailEvents;
  toggleShowBookHistoryListModal(true);
};

// 예약 취소 처리
const updateKey = ref(0);
const onCompleteBookCancel = async () => {
  toggleShowBookHistoryDetail(false);
  if (shouldShowBookHistoryListModal.value) {
    toggleShowBookHistoryListModal(false);
  }
  await getBookInfos();
  updateKey.value += 1;
  showToastMessage("예약이 취소되었습니다");
};

const __returned__ = { boardStore, showErrorModal, showToastMessage, segmentTitles, currentSegmentIndex, onChangedSegmentIndex, get touchStartX() { return touchStartX }, set touchStartX(v) { touchStartX = v }, get touchEndX() { return touchEndX }, set touchEndX(v) { touchEndX = v }, segmentedControl, onTouchStart, onTouchEnd, DISPLAY_TYPE_CALENDAR, DISPLAY_TYPE_LIST, displayTypeMap, displayTypeImageList, displayTypeSelectedImageList, imageAltTextList, selectedDisplayTypeIndexes, currentDisplayType, selectDisplayType, shouldShowFilters, showFilterModal, currentFilters, filters, currentFilterString, onFilterChanged, bookHistoryContentRef, scrollToTop, bookInfos, currentMonthlyBookInfos, requestForCalendar, requestForPastBook, request, getFilteredBookInfos, getBookInfos, eventColorClassMap, currentCalendarEvents, calendarDisplayMode, currentMonth, setCurrentMonth, resetCurrentMonth, changedMonth, boardInfos, isFetchedBoardHistory, shouldShowEmptyContent, limitFromBoardDate, limitToBoardDate, shouldShowFromDateCalendar, shouldShowToDateCalendar, showFromDateCalendar, showToDateCalendar, onSelectFromDate, onSelectToDate, searchFromDate, searchToDate, getBoardInfos, shouldShowBookHistoryDetail, toggleShowBookHistoryDetail, selectedBookInfo, showBookHistoryDetail, shouldShowBoardHistoryDetail, toggleShowBoardHistoryDetail, onCompleteSurvey, selectedBoardInfo, goToBoardEvaluation, showBoardHistoryDetail, shouldShowBookHistoryListModal, toggleShowBookHistoryListModal, selectedBookInfos, showBookHistoryListModal, updateKey, onCompleteBookCancel, Page, NavigationBar, SegmentedControl, BookHistoryDetail, FilterModal, BookHistoryTable, BoardHistoryTable, Calendar, BoardHistoryDetail, EventCalendar, BookHistoryRow, SelectableImageGroup, BookHistoryListModal, PageModal }
Object.defineProperty(__returned__, '__isScriptSetup', { enumerable: false, value: true })
return __returned__
}

})